import { Observable } from 'rxjs';
import { common } from '@qwyk/models';
import {
    Shipment,
    ShipmentData,
    BackendQuery,
    ShipmentState,
    ShipmentTrackingRequest,
    ShipmentTrackingRequestResponse,
} from './+state/shipments.models';
import { HttpCacheConfig, CachedResponse } from '@qwyk/core';

export abstract class ShipmentsServiceBase {
    public abstract getShipments(
        query: BackendQuery,
        cacheConfig?: Partial<HttpCacheConfig>
    ): Observable<common.PaginatedResponse<Shipment>>;

    /**
     * Fetches one shipment from the server by its ID.
     *
     * @param shipmentId
     */
    public abstract getShipment(shipmentId: string): Observable<Shipment>;

    public abstract updateShipmentReferences(
        shipmentId: string,
        update: Partial<Shipment>
    ): Observable<void>;

    public abstract updateShipmentName(
        shipmentId,
        update: Partial<Shipment>
    ): Observable<void>;

    public abstract generateShipmentPdf(shipmentId: string): Observable<Blob>;

    /**
     * Sends a tracking request for a Shipment.
     *
     * @param shipmentId
     * @param trackingRequest
     */
    public abstract sendTrackingRequest(
        shipmentId: string,
        trackingRequest: ShipmentTrackingRequest
    ): Observable<ShipmentTrackingRequestResponse>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public abstract cancelTrackingRequest(shipmentId: string): Observable<any>;

    /**
     * Updates the transport instructions on a ShipmentData object
     *
     * @param shipmentId
     * @param changes
     */
    public abstract updateShipmentDataTransport(
        shipmentId: string,
        changes: Partial<ShipmentData>
    ): Observable<ShipmentData>;

    /**
     * Updates the references on a ShipmentData object
     *
     * @param shipmentId
     * @param changes
     */
    public abstract updateShipmentDataReferences(
        shipmentId: string,
        changes: Partial<ShipmentData>
    ): Observable<ShipmentData>;

    public abstract assignShipmentOperator(
        shipmentId: string,
        operatorId: number
    ): Observable<Shipment>;

    public abstract completeShipment(shipmentId: string): Observable<Shipment>;

    public abstract cancelShipment(shipmentId: string): Observable<Shipment>;

    public abstract loadSysStates(): Observable<ShipmentState[]>;

    public abstract getShipmentsWithMetadata(
        query: BackendQuery,
        cacheConfig: Partial<HttpCacheConfig>
    ): Observable<CachedResponse<common.PaginatedResponse<Shipment>>>;

    public abstract invalidateShipmentsCache(): void;
}
